import { useUserInfo } from '@/domain/account/hooks';
import { type AuthUser } from '@/domain/account/types';
import { isSessionExpired } from '@/features/auth/session-recovery';
import { ApiError, HttpStatusCode } from '@/services/ApiError';
import { isUserAdmin } from '@/utils/permissions';
import { useSession } from 'next-auth/react';
import { createContext, type ReactNode, useMemo } from 'react';

/**
 * How far the app has got in establishing who the visitor is. One value rather than a pile of
 * booleans because the states are mutually exclusive and the routing gates have to agree on their
 * precedence: read as separate flags, "expired" and "loading" and "rejected" were all true at once
 * at various moments, and each gate picked its own winner.
 *
 * - `loading`   nothing to decide on yet
 * - `expired`   signed in, but the refresh token is dead — nothing read with it can be trusted
 * - `rejected`  the token authenticated and no local user matches it
 * - `anonymous` no session
 * - `ready`     signed in with a resolved backend user
 */
export type AuthState = 'loading' | 'expired' | 'rejected' | 'anonymous' | 'ready';

type AuthContextType = {
  authState: AuthState;
  isLoggedIn: boolean;
  isAdmin: boolean;
  user: AuthUser | undefined;
};

type AuthContextProviderProps = {
  children: ReactNode;
};

export const AuthContext = createContext<AuthContextType>({
  authState: 'loading',
  isAdmin: false,
  isLoggedIn: false,
  user: undefined,
});

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const { status, data: session } = useSession();
  const isAuthenticated = status === 'authenticated';
  const { data: user, isLoading: isUserLoading, error: userError } = useUserInfo(isAuthenticated);
  // next-auth reports status 'loading' for the whole of every update() call, not just the first read,
  // and keeps exposing the previous session while it does. Treating that as "not loaded yet" made the
  // page unmount mid-session — throwing away the unsaved work that SessionExpiredDialog promises to
  // keep, since recovering the session is what calls update(). Only a load with no session to show is
  // an initial load.
  const isInitialSessionLoad = status === 'loading' && session == null;
  const isSignedIn = status !== 'unauthenticated' && session?.user != null;
  const isSessionDead = isSessionExpired(session);
  // Only a 403 from GET /account/user means the token authenticated but no local user resolved. Any
  // other failure (500, network) must reach the app's ordinary error handling instead of telling the
  // user their account does not exist.
  const isBackendRejected = userError instanceof ApiError && userError.statusCode === HttpStatusCode.Forbidden;

  const authState = resolveAuthState({
    isInitialSessionLoad,
    isSessionDead,
    isSignedIn,
    isBackendRejected,
    isUserPending: isAuthenticated && isUserLoading,
  });

  const values = useMemo(
    // Deliberately not keyed on `session`: next-auth hands out a new session object on every poll,
    // so depending on it re-rendered every consumer every five minutes for nothing.
    () => ({ authState, isLoggedIn: isSignedIn, isAdmin: isUserAdmin(user), user }),
    [authState, isSignedIn, user],
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

// A dead session outranks a pending user read: the read cannot succeed with a token Keycloak will not
// renew, and reporting `loading` instead would unmount the page the dialog is recovering.
function resolveAuthState({
  isInitialSessionLoad,
  isSessionDead,
  isSignedIn,
  isBackendRejected,
  isUserPending,
}: {
  isInitialSessionLoad: boolean;
  isSessionDead: boolean;
  isSignedIn: boolean;
  isBackendRejected: boolean;
  isUserPending: boolean;
}): AuthState {
  if (isInitialSessionLoad) return 'loading';
  if (isSessionDead) return 'expired';
  if (!isSignedIn) return 'anonymous';
  if (isBackendRejected) return 'rejected';
  if (isUserPending) return 'loading';
  return 'ready';
}
